import { motion } from 'framer-motion';
import Footer from '../../components/Footer';
import { Helmet } from 'react-helmet';
import { useEffect } from 'react';

export function ExternalCalculators() {
  // Track page view when component mounts
  useEffect(() => {
    // If you have analytics, add page view tracking here
    document.title = "Educational Calculators | External Calculator Resources";
  }, []);

  const calculators = [
    {
      title: 'Attendance Calculator',
      description: 'Calculate attendance percentage, required attendance to reach targets, and more with this comprehensive tool.',
      url: 'https://nexuscalc.com/education/attendance',
      icon: '📅',
      category: 'education-management'
    },
    {
      title: 'Student Loan Calculator',
      description: 'Plan your student loan repayment, calculate interest, and visualize payment schedules with this detailed calculator.',
      url: 'https://nexuscalc.com/education/loans',
      icon: '💰',
      category: 'financial'
    },
    {
      title: 'GPA Calculator',
      description: 'Calculate your Grade Point Average (GPA) with this easy-to-use tool. Supports different grading scales and weighting systems.',
      url: 'https://nexuscalc.com/education/gpa',
      icon: '🎓',
      category: 'academic-performance'
    },
    {
      title: 'Class Rank Calculator',
      description: 'Estimate your class rank based on GPA and class size. Helpful for college applications and scholarships.',
      url: 'https://nexuscalc.com/education/rank',
      icon: '📊',
      category: 'academic-performance'
    },
    {
      title: 'Scholarship Calculator',
      description: 'Estimate potential scholarship amounts based on academic performance, extracurricular activities, and other factors.',
      url: 'https://nexuscalc.com/education/scholarship',
      icon: '🏆',
      category: 'financial'
    },
    {
      title: 'Study Time Calculator',
      description: 'Plan your study schedule with this calculator that helps determine optimal study hours based on course load and difficulty.',
      url: 'https://nexuscalc.com/education/studytime',
      icon: '⏱️',
      category: 'education-management'
    },
    {
      title: 'College Cost Calculator',
      description: 'Estimate the total cost of college education including tuition, room and board, books, and other expenses.',
      url: 'https://nexuscalc.com/education/collegecost',
      icon: '💵',
      category: 'financial'
    },
    {
      title: 'Graduation Date Calculator',
      description: 'Calculate your expected graduation date based on credits completed and remaining requirements.',
      url: 'https://nexuscalc.com/education/graduation',
      icon: '📆',
      category: 'education-management'
    },
    {
      title: 'Credit Hour Calculator',
      description: 'Plan your course load and calculate total credit hours for upcoming semesters.',
      url: 'https://nexuscalc.com/education/credithours',
      icon: '📚',
      category: 'academic-planning'
    },
    {
      title: 'Academic Probation Calculator',
      description: 'Calculate the GPA needed to get off academic probation or maintain good academic standing.',
      url: 'https://nexuscalc.com/education/probation',
      icon: '⚠️',
      category: 'academic-performance'
    },
    {
      title: 'Degree Progress Calculator',
      description: 'Track your progress toward degree completion with this comprehensive calculator.',
      url: 'https://nexuscalc.com/education/degreeprogress',
      icon: '📈',
      category: 'academic-planning'
    },
    {
      title: 'Course Grade Calculator',
      description: 'Calculate your final course grade based on assignment weights, exam scores, and remaining assessments.',
      url: 'https://nexuscalc.com/education/coursegrade',
      icon: '📝',
      category: 'academic-performance'
    }
  ];

  // Group calculators by category for better organization
  const categories = {
    'financial': {
      title: 'Financial Calculators',
      description: 'Tools for calculating student loans, scholarships, and college costs'
    },
    'academic-performance': {
      title: 'Academic Performance Calculators',
      description: 'Tools for calculating GPA, class rank, and course grades'
    },
    'education-management': {
      title: 'Education Management Calculators',
      description: 'Tools for managing attendance, study time, and graduation planning'
    },
    'academic-planning': {
      title: 'Academic Planning Calculators',
      description: 'Tools for planning degree progress and credit hours'
    }
  };

  const groupedCalculators = {};
  calculators.forEach(calculator => {
    if (!groupedCalculators[calculator.category]) {
      groupedCalculators[calculator.category] = [];
    }
    groupedCalculators[calculator.category].push(calculator);
  });

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Add Helmet for enhanced SEO */}
      <Helmet>
        <title>Educational Calculators | External Calculator Resources</title>
        <meta name="description" content="Access a collection of specialized educational calculators for attendance tracking, student loans, GPA calculation, and more. Links to trusted external calculator resources." />
        <meta name="keywords" content="attendance calculator, student loan calculator, GPA calculator, education calculators, college cost calculator, academic planning tools" />
        <link rel="canonical" href="https://mocktestpro.com/tools/external-calculators" />
        
        {/* Open Graph tags for social sharing */}
        <meta property="og:title" content="Educational Calculators | External Calculator Resources" />
        <meta property="og:description" content="Access specialized educational calculators for attendance, student loans, GPA, and more." />
        <meta property="og:url" content="https://mocktestpro.com/tools/external-calculators" />
        <meta property="og:type" content="website" />
        
        {/* Structured data for rich results */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "itemListElement": calculators.map((calculator, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "item": {
                "@type": "SoftwareApplication",
                "name": calculator.title,
                "description": calculator.description,
                "applicationCategory": "EducationalApplication",
                "operatingSystem": "Web",
                "url": calculator.url
              }
            }))
          })}
        </script>
      </Helmet>

      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-12"
      >
        <h1 className="text-4xl font-bold text-gray-800 mb-3">
          Educational Calculator Resources
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Access specialized educational calculators from trusted external sources. 
          These tools help with attendance tracking, student loans, GPA calculation, and more.
        </p>
      </motion.div>

      <section className="mb-12 bg-blue-50 p-6 rounded-lg">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">External Calculator Resources</h2>
        <div className="bg-white p-5 rounded-lg shadow">
          <p className="text-gray-700">
            The calculators listed below are hosted on external websites that specialize in educational tools. 
            We've curated this collection to provide you with resources beyond our own calculator suite. 
            Each link will open in a new tab, taking you to the respective calculator on its original website.
          </p>
        </div>
      </section>

      {/* Display calculators by category */}
      {Object.keys(categories).map((categoryKey) => (
        <section key={categoryKey} className="mb-12">
          <motion.h2
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2"
          >
            {categories[categoryKey].title}
          </motion.h2>
          <p className="text-gray-600 mb-6">{categories[categoryKey].description}</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {groupedCalculators[categoryKey]?.map((calculator, index) => (
              <motion.div
                key={calculator.url}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <a
                  href={calculator.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block h-full bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow p-6"
                  aria-label={`Use ${calculator.title}`}
                >
                  <div className="text-4xl mb-4">{calculator.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    {calculator.title}
                  </h3>
                  <p className="text-gray-600 mb-3">
                    {calculator.description}
                  </p>
                  <div className="flex items-center text-blue-600 text-sm">
                    <span>Visit calculator</span>
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                    </svg>
                  </div>
                </a>
              </motion.div>
            ))}
          </div>
        </section>
      ))}

      <section className="my-12 bg-gray-50 p-6 rounded-lg">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Why Use These External Calculators?</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white p-5 rounded-lg shadow">
            <div className="text-3xl mb-2">🔍</div>
            <h3 className="text-lg font-semibold mb-2">Specialized Tools</h3>
            <p className="text-gray-600">These calculators are designed specifically for their respective educational purposes with detailed features.</p>
          </div>
          <div className="bg-white p-5 rounded-lg shadow">
            <div className="text-3xl mb-2">🔄</div>
            <h3 className="text-lg font-semibold mb-2">Regularly Updated</h3>
            <p className="text-gray-600">These external resources are maintained and updated by their respective developers.</p>
          </div>
          <div className="bg-white p-5 rounded-lg shadow">
            <div className="text-3xl mb-2">🧩</div>
            <h3 className="text-lg font-semibold mb-2">Complementary Resources</h3>
            <p className="text-gray-600">These tools complement our own calculator suite to provide a more comprehensive set of resources.</p>
          </div>
        </div>
      </section>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="mt-12 text-center text-gray-600 bg-white p-6 rounded-lg shadow-sm"
      >
        <h2 className="text-xl font-semibold mb-3">About External Links</h2>
        <p>The calculators linked on this page are hosted on external websites and are not affiliated with MockTestPro.</p>
        <p className="mt-2">We've selected these resources based on their quality and usefulness, but we don't control their content or functionality.</p>
        <p className="mt-4 text-sm">
          <strong>Note:</strong> External calculator links open in a new tab and may have their own terms of service and privacy policies.
        </p>
      </motion.div>

      <section className="my-12">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Looking for Test Score Calculators?</h2>
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-100">
          <p className="text-gray-700 mb-4">
            If you're looking for SAT, ACT, or AP exam score calculators, check out our comprehensive suite of test score calculators.
          </p>
          <a 
            href="/tools" 
            className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors"
          >
            View Test Score Calculators
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
